import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PremiumBadge } from "@/components/PremiumBadge";

/**
 * PremiumTeaserCard — a compact card that hints at a Premium feature.
 * Used inline on Dashboard and History (variant='card' vs variant='prominent').
 *
 * Props:
 *   Icon (Lucide) — accent icon
 *   title / description — plain strings (localised by caller)
 *   ctaLabel — text for the CTA
 *   variant — 'compact' (whole card is clickable) or 'prominent' (button CTA)
 *   testId
 */
export function PremiumTeaserCard({
  Icon,
  title,
  description,
  ctaLabel,
  variant = "compact",
  testId,
}) {
  const navigate = useNavigate();
  const goPremium = () => navigate("/premium");

  if (variant === "prominent") {
    return (
      <div
        className="dc-card relative overflow-hidden p-5"
        data-testid={testId}
      >
        <div
          className="absolute -top-14 -right-14 w-40 h-40 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(244,201,93,0.22) 0%, rgba(244,201,93,0) 70%)",
          }}
        />
        <div className="relative flex items-start gap-3">
          <span
            className="flex-none w-10 h-10 rounded-xl flex items-center justify-center"
            style={{
              background:
                "linear-gradient(135deg, rgba(244,201,93,0.22), rgba(167,139,250,0.18))",
            }}
          >
            <Icon size={18} className="text-[#F4C95D]" strokeWidth={1.6} />
          </span>
          <div className="flex-1 min-w-0">
            <p
              className="text-[14px] text-[#F8FAFC]/95 leading-relaxed"
              style={{ letterSpacing: "-0.005em" }}
            >
              {title}
            </p>
            <div className="mt-3">
              <PremiumBadge tone="gold" />
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={goPremium}
          data-testid={testId ? `${testId}-cta` : undefined}
          className="group mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium text-white/85 border border-white/12 hover:bg-white/[0.05] hover:text-white transition-colors duration-200"
        >
          {ctaLabel}
          <ArrowRight
            size={14}
            strokeWidth={1.7}
            className="transition-transform duration-300 group-hover:translate-x-0.5"
          />
        </button>
      </div>
    );
  }

  // compact — clickable card
  return (
    <button
      type="button"
      onClick={goPremium}
      data-testid={testId}
      className="dc-card group w-full text-left relative overflow-hidden p-4 transition-colors duration-200 hover:bg-white/[0.02]"
      style={{ borderColor: "rgba(167, 139, 250, 0.16)" }}
    >
      <div
        className="absolute -top-10 -right-10 w-32 h-32 rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(167,139,250,0.18) 0%, rgba(167,139,250,0) 70%)",
        }}
      />
      <div className="relative flex items-center gap-3">
        <span
          className="flex-none w-9 h-9 rounded-xl flex items-center justify-center"
          style={{
            background:
              "linear-gradient(135deg, rgba(167,139,250,0.20), rgba(244,201,93,0.14))",
          }}
        >
          <Icon size={16} className="text-[#A78BFA]" strokeWidth={1.6} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3
              className="text-[14px] font-medium text-[#F8FAFC] truncate"
              style={{ letterSpacing: "-0.005em" }}
            >
              {title}
            </h3>
          </div>
          {description ? (
            <p className="mt-0.5 text-[12px] text-[#94A3B8] leading-snug truncate">
              {description}
            </p>
          ) : null}
        </div>
        <div className="flex-none">
          <PremiumBadge />
        </div>
      </div>
    </button>
  );
}
